import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ChartThemeOverrideContext } from '@revelio/replots';

import {
  PageExportProvider,
  usePageExport,
} from '../../Chart/chart-menu/PageExportProvider';
import { TabVisibilityProvider } from '../../TabVisibilityContext';
import type { ReportFlowCoverParameter } from '../ReportFlowCoverParameters';
import type { ReportFlowDocument } from '../report-flow-types';
import {
  createPrintFrame,
  type PrintFrame,
  settle,
  waitForImages,
} from './print-frame';
import styles from './ReportFlowPrint.module.css';
import {
  ReportFlowPrintContents,
  ReportFlowPrintCover,
  ReportFlowPrintMeasure,
  ReportFlowPrintPage,
} from './ReportFlowPrintPages';
import {
  buildPrintContents,
  PRINT_CONTENT_HEIGHT_PX,
  type PrintPageEntry,
  paginatePrintBlocks,
  reportFlowBlocks,
  toPrintBlock,
} from './report-flow-print-layout';

const CHART_SETTLE_MS = 300;
const SLICE_BOUNDARY_SELECTOR =
  'p, h1, h2, h3, h4, h5, h6, li, blockquote, pre, table, img, hr';
const FIRST_CONTENT_PAGE = 3;

type PrintSession =
  | {
      phase: 'measure';
      frame: PrintFrame;
      onMeasured: (pages: PrintPageEntry[][]) => void;
      onFailed: (error: unknown) => void;
    }
  | {
      phase: 'print';
      frame: PrintFrame;
      pages: PrintPageEntry[][];
      onRendered: () => void;
      onFailed: (error: unknown) => void;
    };

const formatPrintDate = () => new Date().toLocaleDateString('en-US');

const PrintMeasurePhase = ({
  doc,
  frame,
  onMeasured,
  onFailed,
}: {
  doc: ReportFlowDocument;
  frame: PrintFrame;
  onMeasured: (pages: PrintPageEntry[][]) => void;
  onFailed: (error: unknown) => void;
}) => {
  const { waitForPageDataReady } = usePageExport();
  const measureRef = useRef<HTMLDivElement>(null);
  const blocks = useMemo(() => reportFlowBlocks(doc), [doc]);
  const printBlocks = useMemo(() => blocks.map(toPrintBlock), [blocks]);

  useEffect(() => {
    let cancelled = false;
    const measure = async () => {
      await settle();
      await waitForPageDataReady({ quiet: true });
      await frame.syncStyles();
      await settle();
      const root = measureRef.current;
      if (cancelled || !root) return;
      const measuredById = new Map<
        string,
        { heightPx: number; cutOffsetsPx?: number[] }
      >();
      for (const node of Array.from(
        root.querySelectorAll<HTMLElement>('[data-print-block-id]')
      )) {
        const id = node.dataset.printBlockId;
        if (!id) continue;
        const rect = node.getBoundingClientRect();
        measuredById.set(id, {
          heightPx: rect.height,
          ...(rect.height > PRINT_CONTENT_HEIGHT_PX && {
            cutOffsetsPx: Array.from(
              node.querySelectorAll<HTMLElement>(SLICE_BOUNDARY_SELECTOR)
            ).map((child) => child.getBoundingClientRect().top - rect.top),
          }),
        });
      }
      onMeasured(
        paginatePrintBlocks(
          blocks.map((block) => ({
            id: block.id,
            heightPx: measuredById.get(block.id)?.heightPx ?? 0,
            cutOffsetsPx: measuredById.get(block.id)?.cutOffsetsPx,
            startsPage: block.startsPage,
          }))
        )
      );
    };
    measure().catch((error: unknown) => {
      if (!cancelled) onFailed(error);
    });
    return () => {
      cancelled = true;
    };
  }, [blocks, frame, onMeasured, onFailed, waitForPageDataReady]);

  return (
    <ReportFlowPrintMeasure blocks={printBlocks} measureRef={measureRef} />
  );
};

const PrintPagesPhase = ({
  doc,
  parameters,
  frame,
  pages,
  onRendered,
  onFailed,
}: {
  doc: ReportFlowDocument;
  parameters: ReportFlowCoverParameter[];
  frame: PrintFrame;
  pages: PrintPageEntry[][];
  onRendered: () => void;
  onFailed: (error: unknown) => void;
}) => {
  const { waitForPageDataReady } = usePageExport();
  const dateLabel = useMemo(formatPrintDate, []);

  const blockById = useMemo(
    () =>
      new Map(
        reportFlowBlocks(doc).map((block) => [block.id, toPrintBlock(block)])
      ),
    [doc]
  );
  const contents = buildPrintContents(doc, pages, FIRST_CONTENT_PAGE);
  const pageOffset =
    contents.length > 0 ? FIRST_CONTENT_PAGE : FIRST_CONTENT_PAGE - 1;

  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      await settle();
      await waitForPageDataReady();
      await frame.syncStyles();
      await waitForImages(frame.document);
      await new Promise((resolve) => setTimeout(resolve, CHART_SETTLE_MS));
      await settle();
      if (!cancelled) onRendered();
    };
    render().catch((error: unknown) => {
      if (!cancelled) onFailed(error);
    });
    return () => {
      cancelled = true;
    };
  }, [frame, onRendered, onFailed, waitForPageDataReady]);

  return (
    <>
      <ReportFlowPrintCover
        doc={doc}
        parameters={[...parameters, { label: 'Date', value: dateLabel }]}
      />
      {contents.length > 0 && <ReportFlowPrintContents entries={contents} />}
      {pages.map((entries, pageIndex) => (
        <ReportFlowPrintPage
          key={`${entries[0]?.id}:${entries[0]?.slice?.offsetPx ?? 0}`}
          entries={entries.flatMap(({ id, slice }) => {
            const block = blockById.get(id);
            return block ? [{ block, slice }] : [];
          })}
          pageNumber={pageOffset + pageIndex}
          footerLabel={`${doc.title}  |  ${dateLabel}`}
        />
      ))}
    </>
  );
};

export const ReportFlowPrintHost = ({
  doc,
  parameters,
}: {
  doc: ReportFlowDocument;
  parameters: ReportFlowCoverParameter[];
}) => {
  const { setFlowReportPrinter, waitForPageDataReady } = usePageExport();
  const [session, setSession] = useState<PrintSession | null>(null);

  const titleRef = useRef(doc.title);
  titleRef.current = doc.title;

  useEffect(() => {
    let unmounted = false;
    let activeFrame: PrintFrame | null = null;
    const print = async () => {
      await waitForPageDataReady();
      if (unmounted) return;
      const frame = await createPrintFrame({
        frameClassName: styles.printFrame,
        bodyClassName: styles.printBody,
      });
      if (unmounted) {
        frame.dispose();
        return;
      }
      activeFrame = frame;
      try {
        await frame.syncStyles();
        const pages = await new Promise<PrintPageEntry[][]>(
          (onMeasured, onFailed) => {
            setSession({ phase: 'measure', frame, onMeasured, onFailed });
          }
        );
        await new Promise<void>((onRendered, onFailed) => {
          setSession({ phase: 'print', frame, pages, onRendered, onFailed });
        });
        await frame.print(titleRef.current);
      } finally {
        setSession(null);
        frame.dispose();
        activeFrame = null;
      }
    };
    setFlowReportPrinter(print);
    return () => {
      unmounted = true;
      setFlowReportPrinter(null);
      activeFrame?.dispose();
    };
  }, [setFlowReportPrinter, waitForPageDataReady]);

  if (!session) return null;

  return createPortal(
    <ChartThemeOverrideContext.Provider value="light">
      <PageExportProvider>
        <TabVisibilityProvider isVisible>
          {session.phase === 'measure' ? (
            <PrintMeasurePhase
              doc={doc}
              frame={session.frame}
              onMeasured={session.onMeasured}
              onFailed={session.onFailed}
            />
          ) : (
            <PrintPagesPhase
              doc={doc}
              parameters={parameters}
              frame={session.frame}
              pages={session.pages}
              onRendered={session.onRendered}
              onFailed={session.onFailed}
            />
          )}
        </TabVisibilityProvider>
      </PageExportProvider>
    </ChartThemeOverrideContext.Provider>,
    session.frame.mountNode
  );
};
